import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil } from "lucide-react";
import AdminLayout from "../../commponents/layout/adminLayout";
import DataTable from "../../commponents/table/DataTable";
import type { TableColumn } from "../../commponents/table/types";
import { getHomePage } from "../../api/homePageApi";
import { HOME_SECTIONS } from "../../helpers/homePageSections";
import { showError } from "../../utils/ alert";
import type {
  HomeSectionConfig,
  HomeSectionData,
  HomeSectionKey,
} from "../../type/homePage";

interface HomeSectionRow {
  id: HomeSectionKey;
  no: number;
  page: string;
  section: string;
  content: string;
  updatedAt: string;
}

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

/** Ringkasan isi section, mis. "Wording, Image, 4 Card". */
const describeContent = (
  config: HomeSectionConfig,
  data?: HomeSectionData
) => {
  const parts: string[] = [];

  const allFields = [...config.fields, ...(config.items?.fields ?? [])];

  if (allFields.some((field) => field.type !== "image")) parts.push("Wording");
  if (allFields.some((field) => field.type === "image")) parts.push("Image");

  if (config.items && Array.isArray(data?.items)) {
    parts.push(`${data!.items!.length} ${config.items.itemLabel}`);
  }

  return parts.join(", ");
};

export default function HomePageList() {
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sectionData, setSectionData] = useState<
    Partial<Record<HomeSectionKey, HomeSectionData>>
  >({});

  useEffect(() => {
    const fetchHomePage = async () => {
      try {
        const { data } = await getHomePage();

        setSectionData(data?.data ?? {});
      } catch (error) {
        showError("Failed", "Failed to Load data.");
      }
    };

    fetchHomePage();
  }, []);

  const rows: HomeSectionRow[] = useMemo(
    () =>
      HOME_SECTIONS.map((config, index) => ({
        id: config.key,
        no: index + 1,
        page: config.page ?? "Home",
        section: config.title,
        content: describeContent(config, sectionData[config.key]),
        updatedAt: formatDateTime(sectionData[config.key]?.updated_at),
      })),
    [sectionData]
  );

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));

  const pagedRows = rows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const columns: TableColumn<HomeSectionRow>[] = [
    {
      key: "no",
      title: "No",
    },
    {
      key: "page",
      title: "Page",
    },
    {
      key: "section",
      title: "Section",
      render: (row) => {
        const config = HOME_SECTIONS.find((item) => item.key === row.id);

        return (
          <div>
            <p className="font-semibold">{row.section}</p>

            <p className="text-sm text-gray-500">{config?.description}</p>
          </div>
        );
      },
    },
    {
      key: "content",
      title: "Content",
    },
    {
      key: "updatedAt",
      title: "Last Updated",
    },
    {
      key: "id",
      title: "Action",
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/home-page/edit/${row.id}`)}
            aria-label={`Edit ${row.section}`}
            className="rounded-lg bg-yellow-100 p-2 text-yellow-600 hover:bg-yellow-200">
            <Pencil size={18} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <AdminLayout>
      <div className="bg-white rounded-3xl p-6">
        <DataTable
          title="Website Page Sections"
          columns={columns}
          data={pagedRows}
          showAdd={false}
          showSearch={false}
          showFilter={false}
          pagination={{
            currentPage,
            totalPages,
            pageSize,
            onPageChange: setCurrentPage,
            onPageSizeChange: (size) => {
              setPageSize(size);
              setCurrentPage(1);
            },
          }}
        />
      </div>
    </AdminLayout>
  );
}
